/**
 * lineMarkers.ts — PLAT-12034 (epic PLAT-10484, DE1 / DE3).
 *
 * A dubbed line's **delivery markers** as data: where each marker sits among
 * the line's words, read from the marked-up text the dub stored as the line's
 * direction (`{ kind: 'tags', taggedText }`, PLAT-11798), and placed onto the
 * translation fragments the review draws, so the transcript can show them as
 * chips inside the words and write them back after a chip is removed.
 *
 * Plain functions: no React, no MobX, no network.
 *
 * Words used below, each defined once:
 *
 * - a **marker** is one bracketed cue inside a line, `[whispers]`; its **tag**
 *   is the text inside the brackets.
 * - a line's **words** are its text with every marker taken out and spacing
 *   collapsed — exactly what `stripDeliveryMarkers` answers.
 * - a marker sits **before word `k`**: the k-th word (0-based) of the line's
 *   words follows it. `k === words.length` means it trails the line.
 * - a **fragment** is one translation phrase the review draws; a line's text
 *   is its fragments joined (`lineFragmentRanges`), each with a `[start, end)`
 *   in that text.
 */

import { dropSuperseded } from '@src/editor/dubbing/DubPlacementService';

import { stripDeliveryMarkers, taggedTextForLine } from './directionPrefill';

import type { DubManifestDTO } from '@src/repositories/DubbingRepository';

export interface LineMarker {
  /** The text inside the brackets, trimmed: `whispers`, `short pause`. */
  tag: string;
  /** The word it precedes, 0-based in the line's words; = word count when trailing. */
  beforeWord: number;
}

const MARKER_OR_WORD = /\[([^\]]*)\]|(\S+)/g;

/**
 * The words and the markers of a marked-up line.
 *
 * Tokenizes on whitespace, treating a bracketed run as one marker token
 * wherever it sits (a marker glued to a word, `[sighs]Et`, still counts as
 * before that word). Empty brackets are not a marker.
 */
export function parseLineMarkers(taggedText: string): {
  words: string;
  markers: LineMarker[];
} {
  const words: string[] = [];
  const markers: LineMarker[] = [];
  for (const match of taggedText.matchAll(MARKER_OR_WORD)) {
    const [, tag, word] = match;
    if (tag !== undefined) {
      const trimmed = tag.trim();
      if (trimmed) markers.push({ tag: trimmed, beforeWord: words.length });
      continue;
    }
    if (word) words.push(word);
  }
  return { words: words.join(' '), markers };
}

/**
 * The marked-up line again: the words with each marker written back before
 * the word it precedes, in order. `serialize(parse(x))` is `x` up to spacing.
 */
export function serializeLineMarkers(
  words: string,
  markers: readonly LineMarker[],
): string {
  const tokens = words.split(/\s+/).filter(Boolean);
  const sorted = [...markers].sort((a, b) => a.beforeWord - b.beforeWord);
  const out: string[] = [];
  let next = 0;
  for (let k = 0; k <= tokens.length; k++) {
    while (next < sorted.length && sorted[next].beforeWord <= k) {
      out.push(`[${sorted[next].tag}]`);
      next++;
    }
    if (k < tokens.length) out.push(tokens[k]);
  }
  return out.join(' ');
}

/** One line's markers as the dub stored them, with the words they were written over. */
export interface LineMarkers {
  lineId: string;
  /** The words the markers were written over (de-marked `taggedText`). */
  words: string;
  markers: LineMarker[];
}

/**
 * Every line of the dub that was recorded under a `tags` direction, with its
 * markers — the live recording of each line only, and only when the markup's
 * words are still the words the line was voiced from. Markup over words the
 * line no longer has is left out: the same stale rule the markers editor
 * applies (`markersSeed`, PLAT-11798), since those chips would sit on words
 * that are not there.
 */
export function markersByLine(
  manifest: DubManifestDTO | null | undefined,
): ReadonlyMap<string, LineMarkers> {
  const out = new Map<string, LineMarkers>();
  const directions = manifest?.directions;
  if (!manifest || !directions) return out;
  for (const segment of dropSuperseded(manifest.segments ?? []).live) {
    const record = segment.directionId && directions[segment.directionId];
    const taggedText =
      record && taggedTextForLine(record.direction, segment.segmentId);
    if (!taggedText) continue;
    const { words, markers } = parseLineMarkers(taggedText);
    if (markers.length === 0) continue;
    const voicedFrom = stripDeliveryMarkers(segment.sourceTranslationSnapshot);
    if (voicedFrom !== words) continue;
    out.set(segment.segmentId, { lineId: segment.segmentId, words, markers });
  }
  return out;
}

/** A marker's place on a drawn fragment. */
export interface PlacedMarker {
  lineId: string;
  tag: string;
  /** The marker's index in the line's markers (for removal). */
  index: number;
  /** Character offset in the fragment's own text where the chip sits. */
  offset: number;
}

/** A line's fragments as `lineFragmentRanges` / `buildReviewDubMap` give them. */
export interface LineFragmentRange {
  /** `reviewFragmentKey(phrase)`. */
  key: string;
  start: number;
  end: number;
}

/** Whether the line still reads as the words its markers were written over. */
export function markersFitText(line: LineMarkers, text: string): boolean {
  return stripDeliveryMarkers(text) === line.words;
}

/**
 * Where each marker of a line goes on the review's rows.
 *
 * Needs the line's text as it reads now and its fragments' ranges in it. When
 * the text is still the words the markers were written over, each marker goes
 * to the fragment holding the word it precedes, at that word's offset inside
 * the fragment (a trailing marker goes to the end of the last fragment). When
 * the words differ — the line was edited and is about to be re-dubbed anyway —
 * every marker is shown at the start of the first fragment rather than
 * dropped, so a person still sees the line was directed.
 */
export function placeMarkers(
  line: LineMarkers,
  now: { text: string; fragments: readonly LineFragmentRange[] },
): ReadonlyMap<string, PlacedMarker[]> {
  const out = new Map<string, PlacedMarker[]>();
  if (now.fragments.length === 0) return out;
  const push = (key: string, placed: PlacedMarker) => {
    const list = out.get(key) ?? [];
    list.push(placed);
    out.set(key, list);
  };
  const first = now.fragments[0];
  const last = now.fragments[now.fragments.length - 1];
  const textMatches = markersFitText(line, now.text);
  const wordStarts = textMatches ? wordOffsets(now.text) : null;

  line.markers.forEach((marker, index) => {
    const placed = { lineId: line.lineId, tag: marker.tag, index };
    if (!wordStarts) {
      push(first.key, { ...placed, offset: 0 });
      return;
    }
    if (marker.beforeWord >= wordStarts.length) {
      push(last.key, { ...placed, offset: last.end - last.start });
      return;
    }
    const at = wordStarts[marker.beforeWord];
    const fragment =
      now.fragments.find(f => at >= f.start && at < f.end) ??
      // A boundary the join moved: the nearest fragment that starts after it,
      // else the last one.
      now.fragments.find(f => f.start >= at) ??
      last;
    push(fragment.key, {
      ...placed,
      offset: Math.max(
        0,
        Math.min(at - fragment.start, fragment.end - fragment.start),
      ),
    });
  });
  return out;
}

/** Character offset of each whitespace-delimited word in `text`. */
function wordOffsets(text: string): number[] {
  const starts: number[] = [];
  for (const match of text.matchAll(/\S+/g)) starts.push(match.index ?? 0);
  return starts;
}
